import { Component, Input, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { DocumentReaderComponent } from '../../../document-reader/document-reader.component';
import { DocumentReaderService } from '../../../services/document-reader.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ActivatedRoute } from '@angular/router';
import { LookupFileWizardComponent } from '../../../lookup-file-wizard/lookup-file-wizard.component';
import { MatIconModule } from '@angular/material/icon';
import { AnnuityLookupComponent } from '../../../annuity-lookup/annuity-lookup/annuity-lookup.component';
import { RixtremaLookupComponent } from '../../../rixtrema/rixtrema-lookup/rixtrema-lookup.component';
import { FundFeeHandlerComponent } from '../../../fund-fee-lookup/fund-fee-handler/fund-fee-handler.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-form-document-reader',
  templateUrl: './form-manual-lookup-document.component.html',
  styleUrl: './form-manual-lookup-document.component.scss',
  imports: [
    MatIconModule,
    AnnuityLookupComponent,
    RixtremaLookupComponent,
    FundFeeHandlerComponent,
    MatTooltipModule,
    FormlyModule,
    MatButtonModule,
  ],
})
export class FormlyManualLookupDocumentComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  private docReadSvc = inject(DocumentReaderService);
  private dialog = inject(MatDialog);
  private ss = inject(SessionStorageService);
  private route = inject(ActivatedRoute);

  @Input() index;
  @Input() field;
  varset: any;
  profile: any;
  buttonType: string;
  additionalData = {};
  rights: string[] = [];
  rixtremaButtonTypes = [
    'rixtrema',
    'newPlanSearch',
    'search401k',
    'search403b',
    'searchMP',
    'searchPension',
    'searchESOP',
  ];
  canUseInputWizard = false;

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.profile = params.id;
    });
    this.rights = this.ss.get('rights');
    this.canUseInputWizard =
      this.rights.includes('LookupFileWizard') ||
      this.rights.includes('Combine5500andDocReader');
  }

  buttonClicked(ev, operation, opts = null) {
    // operations:
    // 1 === manual
    // 2 === lookup
    // 3 === upload
    if (!opts) {
      this.formControl.setValue(operation);
      if (
        operation === 3 ||
        operation === '3' ||
        (typeof operation === 'boolean' && operation)
      ) {
        this.openProcessDocDialog();
      }
    } else {
      this.formControl.setValue('4');
      this.openProcessDocDialog(opts);
    }
  }

  saveToModel(data, varset) {
    if (!varset) varset = 'ClientPerson';
    this.model[`${varset}_DocumentReadingJSON`] = JSON.stringify(data);
  }

  getQuestions() {
    let fieldLabels = [];
    if (this.varset === 'InvestmentWizard') {
      fieldLabels = this.field.parent.fieldGroup
        .find(fieldGroup => fieldGroup.key === 'CurrentPosition')
        .fieldArray.fieldGroup.map(fieldItem => {
          return {
            key: fieldItem.key,
            label: fieldItem.props.label,
            hidden: fieldItem.props.hidden,
            type: fieldItem.type,
            props: fieldItem.props,
          };
        });
    } else if (this.varset === 'CurrentAnnuityDetailsArray') {
      fieldLabels = this.field.parent.fieldGroup
        .filter(fieldGroup => fieldGroup.key.includes(this.varset))
        .map(fieldItem => {
          return {
            key: fieldItem.key,
            label: fieldItem.props.label,
            hidden: fieldItem.props.hidden,
            type: fieldItem.type,
            props: fieldItem.props,
          };
        });
    } else {
      //different PP varsets case
      let fundDetailsVarset = null;
      if (this.varset === 'CurrentPlanDetailsArray') {
        fundDetailsVarset = 'CPRolloverFundDetails';
      } else if (this.varset === 'Current403BDetailsArray') {
        fundDetailsVarset = 'CPRollover403bFundDetails';
      } else if (this.varset === 'CurrentMoneyPurchaseDetailsArray') {
        fundDetailsVarset = 'CPRolloverMPFundDetails';
      } else if (this.varset === 'CurrentIRABrokerageDetailsArray') {
        fundDetailsVarset = 'CPRolloverIRABroFundDetails';
      } else if (this.varset === 'CurrentBrokerageDetailsArray') {
        fundDetailsVarset = 'CPRolloverBroFundDetails';
      }
      let subDetails = false;

      const traverseFieldGroups = (
        fieldGroups: any[],
        varset: string | undefined
      ): any[] => {
        let result: any[] = [];
        fieldGroups.forEach(fieldGroup => {
          if (fieldGroup.key?.toLowerCase().includes('funddetails')) {
            subDetails = true;
          }
          if (!varset || fieldGroup.key.includes(varset)) {
            const fieldItem = {
              key: fieldGroup.key,
              label: fieldGroup.props.label || fieldGroup.label || '',
              hidden: fieldGroup.props.hidden,
              type: fieldGroup.type,
              props: fieldGroup.props,
              excludeFromDocReading: fieldGroup.props.excludeFromDocReading,
              fieldGroup: fieldGroup.fieldGroup ? fieldGroup.fieldGroup : null,
              id: fieldGroup.id,
              varset: fieldGroup.varset,
            };
            result.push(fieldItem);
            if (fieldItem.fieldGroup) {
              result = result.concat(
                traverseFieldGroups(fieldItem.fieldGroup, varset)
              );
            }
          } else if (
            Object.prototype.hasOwnProperty.call(fieldGroup, 'fieldGroup')
          ) {
            result = result.concat(
              traverseFieldGroups(fieldGroup.fieldGroup, varset)
            );
          }
        });
        return result;
      };

      // Start traversal from the top-level field group
      fieldLabels = traverseFieldGroups(
        this.field?.parent?.parent?.fieldGroup,
        this.varset
      );

      // If subDetails is true, include additional field groups from 'fundDetails'
      if (subDetails) {
        const fundDetailsGroup = this.field.parent.fieldGroup.find(fieldGroup =>
          fieldGroup.key.includes('fundDetails')
        );

        if (
          fundDetailsGroup &&
          fundDetailsGroup.fieldArray &&
          fundDetailsGroup.fieldArray.fieldGroup
        ) {
          fieldLabels = [
            ...fieldLabels,
            ...fundDetailsGroup.fieldArray.fieldGroup.map(fieldItem => ({
              key: fieldItem.key,
              label: fieldItem.props.label,
              hidden: fieldItem.props.hidden,
              type: fieldItem.type,
              props: fieldItem.props,
              excludeFromDocReading: fieldItem.props.excludeFromDocReading,
            })),
          ];
        }
      }
    }
    return fieldLabels;
  }

  openProcessDocDialog(opts: any = null) {
    if (!opts || (opts && opts.find(opt => opt.value === '3'))) {
      if (this.ss.get('currentApp') !== 'rb') {
        this.varset = this.field.key.split('_')[0];
      }

      if (
        this.varset === 'CurrentPlanDetailsArray' ||
        this.varset === 'Current403BDetailsArray' ||
        this.varset === 'CurrentIRABrokerageDetailsArray' ||
        this.varset === 'CurrentIRAAdvisoryDetailsArray' ||
        this.varset === 'CurrentIRADirectDetailsArray' ||
        this.varset === 'CurrentSEPDetailsArray' ||
        this.varset === 'CurrentSIMPLEDetailsArray' ||
        this.varset === 'CurrentMoneyPurchaseDetailsArray' ||
        this.varset === 'CurrentBrokerageDetailsArray' ||
        this.varset === 'CurrentAdvisoryDetailsArray' ||
        this.varset === 'CurrentDirectDetailsArray' ||
        this.varset === 'CurrentNonERISADetailsArray' ||
        this.varset === 'CurrentOtherDetailsArray'
      ) {
        let fundDetailsVarset = null;
        switch (this.varset) {
          case 'CurrentPlanDetailsArray':
            fundDetailsVarset = 'CPRolloverFundDetails';
            break;
          case 'Current403BDetailsArray':
            fundDetailsVarset = 'CPRollover403bFundDetails';
            break;
          case 'CurrentIRABrokerageDetailsArray':
            fundDetailsVarset = 'CPRolloverIRABroFundDetails';
            break;
          case 'CurrentIRAAdvisoryDetailsArray':
            fundDetailsVarset = 'CPRolloverIRAAdvFundDetails';
            break;
          case 'CurrentIRADirectDetailsArray':
            fundDetailsVarset = 'CPRolloverIRADirFundDetails';
            break;
          case 'CurrentSEPDetailsArray':
            fundDetailsVarset = 'CPRolloverSEPFundDetails';
            break;
          case 'CurrentSIMPLEDetailsArray':
            fundDetailsVarset = 'CPRolloverSIMPLEFundDetails';
            break;
          case 'CurrentMoneyPurchaseDetailsArray':
            fundDetailsVarset = 'CPRolloverMPFundDetails';
            break;
          case 'CurrentBrokerageDetailsArray':
            fundDetailsVarset = 'CPRolloverBroFundDetails';
            break;
          case 'CurrentAdvisoryDetailsArray':
            fundDetailsVarset = 'CPRolloverAdvFundDetails';
            break;
          case 'CurrentDirectDetailsArray':
            fundDetailsVarset = 'CPRolloverDirFundDetails';
            break;
          case 'CurrentNonERISADetailsArray':
            fundDetailsVarset = 'CPRolloverNEFundDetails';
            break;
          case 'CurrentOtherDetailsArray':
            fundDetailsVarset = 'CPRolloverOtherFundDetails';
            break;

          default:
            break;
        }
        const ppPlanData = {};
        let planType = 'DC';

        let fundType = null;

        if (this.field.key.toLowerCase().includes('rixtrema')) {
          fundType = 'rixtrema';
        } else if (
          this.field.key.toLowerCase().includes('currentplandetailsarray')
        ) {
          fundType = 'search401k';
        } else if (
          this.field.key.toLowerCase().includes('current403bdetailsarray')
        ) {
          fundType = 'search403b';
        } else if (
          this.field.key.toLowerCase().includes('currentesopdetailsarray')
        ) {
          fundType = 'searchESOP';
        } else if (
          this.field.key
            .toLowerCase()
            .includes('currentmoneypurchasedetailsarray')
        ) {
          fundType = 'searchMP';
        } else if (this.field.key.toLowerCase().includes('pension')) {
          fundType = 'searchPension';
        }

        if (fundType === 'searchPension') {
          planType = 'DB';
        }

        ppPlanData['fundType'] = fundType;
        ppPlanData['server'] =
          this.props.model.UnitRolloverSettings_ExternalSource === 1
            ? 'rixtrema'
            : 'rightbridge';
        ppPlanData['groupIndex'] = this.field.parent.form.controls.indexOf(
          this.field.form
        );
        // ppPlanData['groupIndex'] = 0;
        ppPlanData['planType'] = planType;

        ppPlanData['fundDetailsVarset'] = fundDetailsVarset;

        this.additionalData = ppPlanData;
      } else if (
        this.varset === 'CurrentESOPDetailsArray' ||
        this.varset === 'CurrentPensionDetailsArray' ||
        this.varset === 'CurrentAnnuityDetailsArray' ||
        this.varset === 'CurrentLIDetailsArray' ||
        this.varset === 'Current529DetailsArray'
      ) {
        this.additionalData = {
          groupIndex: this.field.parent.form.controls.indexOf(this.field.form),
        };
      }
      let promptName =
        this.field.parent?.parent?.parent?.model?.[
          `UnitAISettings_${this.ss
            .get('currentApp')
            .toUpperCase()}DocumentReadingAIPromptID`
        ] || 'test';

      if (this.varset === 'CurrentAnnuityDetailsArray') {
        promptName =
          this.field.parent?.parent?.parent?.model?.[
            `UnitAISettings_AWDocumentReadingAIPromptID`
          ] || 'test';
      } else if (this.varset === 'CurrentLIDetailsArray') {
        promptName =
          this.field.parent?.parent?.parent?.model?.[
            `UnitAISettings_LIDocumentReadingAIPromptID`
          ] || 'test';
      }

      const ocrType =
        this.field.parent?.parent?.parent?.model?.[
          `UnitAISettings_${this.ss.get('currentApp').toUpperCase()}OCRType`
        ] || null;

      this.additionalData['allowMultiFileUpload'] =
        this.field.parent?.parent?.parent?.model
          ?.UnitAISettings_AllowMultiFileUpload === 'Y';

      this.additionalData['bypassAIProcess'] =
        this.field.parent?.parent?.parent?.model
          ?.UnitAISettings_BypassAIProcess === 'Y';

      this.additionalData['clientFirstName'] =
        this.field.parent?.parent?.parent?.model?.ClientPerson_FirstName || '';

      this.additionalData['clientLastName'] =
        this.field.parent?.parent?.parent?.model?.ClientPerson_LastName || '';

      this.additionalData['dateToday'] = new Date().toISOString();

      this.additionalData['dateRequirement'] =
        this.field.parent?.parent?.parent?.model
          ?.UnitAISettings_DateRequirement ?? null;

      const argData = {
        index: this.field.parent.key,
        group: this.field.templateOptions.group,
        profile: this.profile,
        varset: this.varset,
        modalDisplay: this.props.data,
        uploadSettings: { promptName: promptName, ocrType: ocrType },
        getQuestions: () => this.getQuestions(),
        saveToModel: (response, varset) => this.saveToModel(response, varset),
        additionalData: this.additionalData,
        fieldData: this.field,
        buttonType: this.props.buttonType,
        docReadFundDetailIDs: {
          pageDetail: {
            sectionId:
              this.field.parent?.parent?.parent?.props.sectionId ||
              this.field.parent?.props.sectionId,
            tabId:
              this.field.parent.model[
                this.field.parent.fieldGroup
                  .map(x => x.key)
                  .find((x: string) => x.includes('SourceBridgeID'))
              ],
          },
        },
      };
      if (opts) {
        Object.assign(argData, { opts: opts });
        let dialogRef = this.dialog.open(LookupFileWizardComponent, {
          data: argData,
          panelClass: 'compare-dialog',
          width: '95vw',
          height: '95vh',
          maxHeight: '95vh',
          maxWidth: '95vw',
          minHeight: '95vh',
          minWidth: '95vw',
          disableClose: true,
        });
        dialogRef.afterClosed().subscribe(result => {
          dialogRef = null;
          if (result) {
            if (result === 'cancel') {
              this.formControl.setValue(1);
            } else if (
              Object.prototype.hasOwnProperty.call(result, 'fileSaved')
            ) {
              if (
                Object.prototype.hasOwnProperty.call(
                  this.form.controls,
                  `${this.field.key.split('_')[0]}_DocumentUploadSuccess`
                )
              ) {
                this.form.controls[
                  `${this.field.key.split('_')[0]}_DocumentUploadSuccess`
                ].setValue(true);
              }
            } else if (
              Object.prototype.hasOwnProperty.call(
                result,
                'DocumentReadingJSON'
              )
            ) {
              this.saveToModel(result['DocumentReadingJSON'], this.varset);
            }
          }
        });
      } else {
        let dialogRef = this.dialog.open(DocumentReaderComponent, {
          data: argData,
          panelClass: 'compare-dialog',
          width: '95vw',
          height: '95vh',
          maxHeight: '95vh',
          maxWidth: '95vw',
          minHeight: '95vh',
          minWidth: '95vw',
          disableClose: true,
        });
        dialogRef.afterClosed().subscribe(result => {
          dialogRef = null;
          if (result === 'cancel') {
            this.formControl.setValue(1);
          } else if (
            result &&
            Object.prototype.hasOwnProperty.call(result, 'fileSaved')
          ) {
            if (
              Object.prototype.hasOwnProperty.call(
                this.form.controls,
                `${this.field.key.split('_')[0]}_DocumentUploadSuccess`
              )
            ) {
              this.form.controls[
                `${this.field.key.split('_')[0]}_DocumentUploadSuccess`
              ].setValue(true);
            }
          } else if (result) {
            this.docReadSvc.docDataDetail({
              data: result,
              pageDetail: {
                sectionId:
                  this.field.parent?.parent?.parent?.props.sectionId ||
                  this.field.parent?.props.sectionId,
                tabId:
                  this.field.parent.model[
                    this.field.parent.fieldGroup
                      .map(x => x.key)
                      .find((x: string) => x.includes('SourceBridgeID'))
                  ],
              },
            });
          }
        });
      }
    } else if (opts) {
      if (this.ss.get('currentApp') !== 'rb') {
        this.varset = this.field.key.split('_')[0];
      }
      const argData = {
        index: this.field.parent.key,
        group: this.field.templateOptions.group,
        profile: this.profile,
        varset: this.varset,
        saveToModel: (response, varset) => this.saveToModel(response, varset),
        additionalData: this.additionalData,
        fieldData: this.field,
        buttonType: this.props.buttonType,
      };
      Object.assign(argData, { opts: opts });
      let dialogRef = this.dialog.open(LookupFileWizardComponent, {
        data: argData,
        panelClass: 'compare-dialog',
        width: '95vw',
        height: '95vh',
        maxHeight: '95vh',
        maxWidth: '95vw',
        minHeight: '95vh',
        minWidth: '95vw',
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe(result => {
        dialogRef = null;
        if (result === 'cancel') {
          this.formControl.setValue(1);
        }
      });
    }
  }
}
